/**
 * <Minerve expression="veille" action="scrutin" accessoire="loupe" :taille="96" />
 *
 * La mascotte officielle de Follaw, en SVG pur (charte « Minerve-Systeme-mascotte-v3 », 18/10/2026).
 * Le dessin vit dans `minerve-dessin.ts`, extrait du script de la charte. Ce composant ne fait que :
 *  - traduire les noms du Studio (section 05) en mouvements ;
 *  - choisir le niveau de detail selon la taille (section 16) ;
 *  - couper toute animation quand l'utilisateur a demande « reduire les animations » (section 04).
 *
 * Minerve est decorative : elle est masquee aux lecteurs d'ecran. Le texte a cote porte le sens
 * (« Chargement en cours », le titre de l'erreur, le message du toast).
 */
import { defineComponent, onBeforeUnmount, onMounted, ref, type PropType } from 'vue'
import { ACTIONS, dessinerMinerve, detailPourTaille, type Accessoire, type Action, type Detail, type Expression, type Mouvement } from './minerve-dessin'
import './minerve.css'

function preferenceReduite(): MediaQueryList | null {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia('(prefers-reduced-motion: reduce)') : null
}

export default defineComponent({
  name: 'Minerve',
  props: {
    /** L'une des expressions de la section 03 (`neutre`, `veille`, `trouve`, `lecture`, `dort`, `perplexe`, `clin`, `surpris`, `severe`, `etourdi`, `triste`, `pudique`). */
    expression: { type: String as PropType<Expression>, default: 'neutre' },
    /** L'une des actions du Studio (`repos`, `scrutin`, `alerte`, `salut`, `oui`, `non`, `sursaut`, `vol`, `envol`, `ecoute`, `orbite`, `lecture`). */
    action: { type: String as PropType<Action | null>, default: null },
    /** Mouvements bruts, quand la charte en fixe une combinaison precise (ex. erreur 404 : `['tilt', 'blink', 'scan']`). Prioritaire sur `action`. */
    mouvements: { type: Array as PropType<readonly Mouvement[] | null>, default: null },
    accessoire: { type: String as PropType<Accessoire | null>, default: null },
    /** Cote en pixels. */
    taille: { type: Number, default: 96 },
    /** Force le niveau de detail ; par defaut il suit la taille (section 16). */
    detail: { type: String as PropType<Detail | null>, default: null },
    /** Couleur du corps : seulement le blanc des boutons pleins (glyphe en orbite), sinon Eminence. */
    corps: { type: String, default: null },
    /** Faux pour un rendu fige (capture, impression). */
    anime: { type: Boolean, default: true },
  },
  setup(props) {
    const reduit = ref(preferenceReduite()?.matches ?? false)
    const mq = preferenceReduite()
    const suivre = (e: MediaQueryListEvent) => { reduit.value = e.matches }
    onMounted(() => mq?.addEventListener?.('change', suivre))
    onBeforeUnmount(() => mq?.removeEventListener?.('change', suivre))

    return () => {
      const anim: readonly Mouvement[] = props.mouvements ?? (props.action ? ACTIONS[props.action] : [])
      // Le Studio ajoute le balayage a la loupe (section 05).
      const mvts = props.accessoire === 'loupe' && !anim.includes('scan') && !props.mouvements ? [...anim, 'scan' as const] : anim
      const vnode = dessinerMinerve({
        size: props.taille,
        expr: props.expression,
        detail: props.detail ?? detailPourTaille(props.taille),
        anim: mvts,
        body: props.corps ?? undefined,
        prop: props.accessoire,
      }, props.anime && !reduit.value)
      vnode.props = { ...vnode.props, class: 'minerve', 'aria-hidden': 'true', focusable: 'false', 'data-minerve': props.expression }
      return vnode
    }
  },
})
